import { create } from 'zustand';
import { adminSupportApi } from '../api/adminSupportApi';
import type { AdminSupportTicket, AdminSupportTicketThread, TicketStatus } from '../types/domain';
import { IDLE_LOAD, LOADING_LOAD, READY_LOAD, failedLoad, type LoadState } from './loadState';

interface AdminSupportState {
  tickets: AdminSupportTicket[];
  selectedTicketThread: AdminSupportTicketThread | null;
  load: LoadState;
  threadLoad: LoadState;
  threadTicketId: string | null;
  pendingTicketIds: string[];

  loadTickets: () => Promise<void>;
  pollTickets: () => Promise<void>;
  loadTicketThread: (ticketId: string) => Promise<AdminSupportTicketThread>;
  refreshTickets: (selectedTicketId?: string | null) => Promise<void>;
  clearTicketThread: () => void;
  setTicketStatus: (ticketId: string, status: TicketStatus) => Promise<AdminSupportTicket>;
  sendReply: (
    ticketId: string,
    input: { message: string; closeTicket?: boolean }
  ) => Promise<AdminSupportTicketThread>;
  reset: () => void;
}

function upsertTicket(
  tickets: AdminSupportTicket[],
  updated: AdminSupportTicket
): AdminSupportTicket[] {
  const exists = tickets.some((ticket) => ticket.id === updated.id);
  const next = exists
    ? tickets.map((ticket) => (ticket.id === updated.id ? updated : ticket))
    : [updated, ...tickets];
  return [...next].sort((a, b) =>
    (b.updatedAt ?? b.createdAt ?? '').localeCompare(a.updatedAt ?? a.createdAt ?? '')
  );
}

export const useAdminSupportStore = create<AdminSupportState>((set, get) => ({
  tickets: [],
  selectedTicketThread: null,
  load: IDLE_LOAD,
  threadLoad: IDLE_LOAD,
  threadTicketId: null,
  pendingTicketIds: [],

  async loadTickets() {
    set({ load: LOADING_LOAD });
    try {
      const tickets = await adminSupportApi.getTickets();
      set({ tickets, load: READY_LOAD });
    } catch (err) {
      set({ load: failedLoad(err) });
    }
  },

  async pollTickets() {
    try {
      const tickets = await adminSupportApi.getTickets();
      set({ tickets });
    } catch {
      // Background checks leave the last good inbox visible. Manual refresh exposes errors.
    }
  },

  async loadTicketThread(ticketId) {
    set({ threadLoad: LOADING_LOAD, threadTicketId: ticketId });
    try {
      const thread = await adminSupportApi.getTicketThread(ticketId);
      set((state) => {
        if (state.threadTicketId !== ticketId) return state;
        return {
          selectedTicketThread: thread,
          threadLoad: READY_LOAD,
          tickets: upsertTicket(state.tickets, thread),
        };
      });
      return thread;
    } catch (err) {
      set((state) => {
        if (state.threadTicketId !== ticketId) return state;
        return {
          selectedTicketThread: null,
          threadLoad: failedLoad(err),
        };
      });
      throw err;
    }
  },

  async refreshTickets(selectedTicketId) {
    set({ load: LOADING_LOAD });
    try {
      const tickets = await adminSupportApi.getTickets();
      set({ tickets, load: READY_LOAD });
    } catch (err) {
      set({ load: failedLoad(err) });
      return;
    }

    if (!selectedTicketId) return;

    try {
      await get().loadTicketThread(selectedTicketId);
    } catch {
      // The active drawer keeps its own thread error state.
    }
  },

  clearTicketThread() {
    set({ selectedTicketThread: null, threadLoad: IDLE_LOAD, threadTicketId: null });
  },

  async setTicketStatus(ticketId, status) {
    set((s) => ({ pendingTicketIds: [...s.pendingTicketIds, ticketId] }));
    try {
      const updated = await adminSupportApi.updateTicketStatus(ticketId, status);
      set((s) => ({
        tickets: upsertTicket(s.tickets, updated),
        selectedTicketThread:
          s.selectedTicketThread?.id === updated.id
            ? { ...s.selectedTicketThread, ...updated }
            : s.selectedTicketThread,
        pendingTicketIds: s.pendingTicketIds.filter((id) => id !== ticketId),
      }));
      return updated;
    } catch (err) {
      set((s) => ({ pendingTicketIds: s.pendingTicketIds.filter((id) => id !== ticketId) }));
      throw err;
    }
  },

  async sendReply(ticketId, input) {
    set((s) => ({ pendingTicketIds: [...s.pendingTicketIds, ticketId] }));
    try {
      const thread = await adminSupportApi.sendReply(ticketId, input);
      set((s) => ({
        tickets: upsertTicket(s.tickets, thread),
        selectedTicketThread:
          s.selectedTicketThread?.id === ticketId ? thread : s.selectedTicketThread,
        pendingTicketIds: s.pendingTicketIds.filter((id) => id !== ticketId),
      }));
      return thread;
    } catch (err) {
      set((s) => ({ pendingTicketIds: s.pendingTicketIds.filter((id) => id !== ticketId) }));
      throw err;
    }
  },

  reset() {
    set({
      tickets: [],
      selectedTicketThread: null,
      load: IDLE_LOAD,
      threadLoad: IDLE_LOAD,
      threadTicketId: null,
      pendingTicketIds: [],
    });
  },
}));
